import { Box, Button, Card, Chip, CircularProgress, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material'
import { useMemo, useState, type ReactNode } from 'react'
import { toast } from 'react-toastify'
import {
    useCreateProjectNeedSlotMutation,
    useDeleteProjectNeedSlotMutation,
    useGetProjectNeedSlotsQuery,
    useUpdateProjectNeedSlotMutation,
} from '../../../../api/projects'
import { useGetCompetenciesDictionaryQuery, useGetParticipationsDictionaryQuery, useGetResourcesDictionaryQuery } from '../../../../api/dictionaries'
import type { IProjectNeedSlotKind, IProjectNeedSlotResource, IProjectNeedSlotStatus, TDictionaryItem } from '../../../../api/types'
import { DictionaryMultiSelectControl } from '../../../../components/ui-kit/DictionaryMultiSelect'
import { AutocompleteInput } from '../../../../components/ui-kit/AutocompleteInput'
import { russiaRegionsOptions } from '../../../../consts/russiaRegions'
import { CardStyle } from './EditWhoNeededForm/style'

type ProjectNeedSlotsManagerProps = {
    projectId: number
    kind: IProjectNeedSlotKind
    title: string
    legacyItems?: LegacyNeedItem[]
    legacyTitle?: string
}

type LegacyNeedItem = {
    title?: string | null
    description?: string | null
}

type SlotDraft = {
    title: string
    description: string
    city: string
    region: string
    remote_allowed: boolean
    quantity: string
    acquisition_type: string
    duration_type: string
    delivery_type: string
    budget_type: string
    budget_comment: string
    requirements: string
    competency_ids: number[]
    resource_ids: number[]
    participation_ids: number[]
}

const emptyDraft: SlotDraft = {
    title: '',
    description: '',
    city: '',
    region: '',
    remote_allowed: true,
    quantity: '',
    acquisition_type: '',
    duration_type: '',
    delivery_type: '',
    budget_type: '',
    budget_comment: '',
    requirements: '',
    competency_ids: [],
    resource_ids: [],
    participation_ids: [],
}

const statusLabels: Record<IProjectNeedSlotStatus, string> = {
    open: 'Открыта',
    paused: 'Пауза',
    closed: 'Закрыта',
}

const statusColors: Record<IProjectNeedSlotStatus, 'success' | 'warning' | 'default'> = {
    open: 'success',
    paused: 'warning',
    closed: 'default',
}

const resourceAcquisitionLabels: Record<string, string> = {
    gift: 'Получить безвозмездно',
    temporary_use: 'Временно получить',
    rent: 'Арендовать',
    buy: 'Купить',
    barter: 'Обмен/бартер',
    shared_use: 'Совместно использовать',
    access: 'Доступ без передачи',
}

const resourceDurationLabels: Record<string, string> = {
    once: 'Разово',
    period: 'На период',
    permanent: 'Постоянно',
}

const resourceDeliveryLabels: Record<string, string> = {
    delivery: 'Доставка',
    pickup: 'Самовывоз',
    on_site: 'Использовать на месте',
    remote: 'Удалённо',
    discussable: 'Обсуждается',
}

const resourceBudgetLabels: Record<string, string> = {
    free: 'Бесплатно',
    has_budget: 'Есть бюджет',
    discussable: 'Обсуждается',
    barter: 'Бартер',
}

const toSelectOptions = (labels: Record<string, string>) =>
    Object.entries(labels).map(([value, label]) => ({ value, label }))

const resourceAcquisitionOptions = toSelectOptions(resourceAcquisitionLabels)
const resourceDurationOptions = toSelectOptions(resourceDurationLabels)
const resourceDeliveryOptions = toSelectOptions(resourceDeliveryLabels)
const resourceBudgetOptions = toSelectOptions(resourceBudgetLabels)

const actionButtonSx = {
    minWidth: 96,
    px: 1.5,
} as const

const fieldRowItemSx = {
    flex: 1,
    minWidth: 0,
} as const

const dictionaryNames = (items: TDictionaryItem[]) => items.map((item) => item.name).filter(Boolean)

const normalizeText = (value: string | null | undefined) => (value ?? '').trim()
const normalizedKey = (value: string) => value.trim().toLowerCase()

function FieldBlock({ label, children }: { label: string; children: ReactNode }) {
    return (
        <Stack gap={0.75} sx={{ minWidth: 0, width: '100%' }}>
            <Typography variant="black-14-medium" component="div">
                {label}
            </Typography>
            {children}
        </Stack>
    )
}

const FIELD_CONTROL_HEIGHT = 42

const singleLineFieldSx = {
    '& .MuiOutlinedInput-root': {
        minHeight: FIELD_CONTROL_HEIGHT,
        height: FIELD_CONTROL_HEIGHT,
        boxSizing: 'border-box',
    },
} as const

const autocompleteFieldSx = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
        minHeight: FIELD_CONTROL_HEIGHT,
        height: FIELD_CONTROL_HEIGHT,
        boxSizing: 'border-box',
        alignItems: 'center',
        py: 0,
    },
    '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
        py: '0 !important',
    },
    '& .MuiAutocomplete-endAdornment': {
        top: '50%',
        transform: 'translateY(-50%)',
        right: 8,
    },
} as const

function AutocompleteSelectField({
    label,
    value,
    options,
    onChange,
    placeholder,
}: {
    label: string
    value: string
    options: { label: string; value: string }[]
    onChange: (value: string) => void
    placeholder: string
}) {
    return (
        <FieldBlock label={label}>
            <AutocompleteInput
                options={options}
                value={value}
                onChange={(next) => onChange(next == null || next === '' ? '' : String(next))}
                size="small"
                fullWidth
                freeSolo={false}
                disableClearable={false}
                placeholder={placeholder}
                sx={autocompleteFieldSx}
            />
        </FieldBlock>
    )
}

function SlotDictionarySummary({ slot }: { slot: IProjectNeedSlotResource }) {
    const groups = slot.kind === 'role'
        ? [
            { label: 'Компетенции', names: dictionaryNames(slot.competencies) },
            { label: 'Условия участия', names: dictionaryNames(slot.participation) },
        ]
        : [
            { label: 'Ресурсы', names: dictionaryNames(slot.resources) },
        ]
    const visibleGroups = groups.filter((group) => group.names.length > 0)

    if (visibleGroups.length === 0) return null

    return (
        <Stack gap={0.75} sx={{ minWidth: 0 }}>
            {visibleGroups.map((group) => (
                <Typography
                    key={group.label}
                    variant="body2"
                    component="div"
                    sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                >
                    <Typography component="span" variant="body2" color="text.secondary">
                        {group.label}:{' '}
                    </Typography>
                    <Typography component="span" variant="black-14-medium">
                        {group.names.join('; ')}
                    </Typography>
                </Typography>
            ))}
        </Stack>
    )
}

function compactMeta(draft: SlotDraft) {
    const meta = {
        quantity: draft.quantity.trim(),
        acquisition_type: draft.acquisition_type,
        duration_type: draft.duration_type,
        delivery_type: draft.delivery_type,
        budget_type: draft.budget_type,
        budget_comment: draft.budget_comment.trim(),
        requirements: draft.requirements.trim(),
    }

    return Object.fromEntries(Object.entries(meta).filter(([, value]) => value.length > 0))
}

function resourceDetails(slot: IProjectNeedSlotResource): string[] {
    const meta = slot.meta ?? {}
    const details = [
        meta.quantity ? `Количество: ${meta.quantity}` : null,
        meta.acquisition_type ? resourceAcquisitionLabels[meta.acquisition_type] ?? meta.acquisition_type : null,
        meta.duration_type ? resourceDurationLabels[meta.duration_type] ?? meta.duration_type : null,
        meta.delivery_type ? resourceDeliveryLabels[meta.delivery_type] ?? meta.delivery_type : null,
        meta.budget_type ? `Бюджет: ${resourceBudgetLabels[meta.budget_type] ?? meta.budget_type}` : null,
        meta.budget_comment ? `Комментарий: ${meta.budget_comment}` : null,
        slot.city || slot.region ? [slot.city, slot.region].filter(Boolean).join(', ') : null,
    ]

    return details.filter((item): item is string => Boolean(item))
}

function statusLabel(slot: IProjectNeedSlotResource): string {
    if (slot.kind === 'resource' && slot.status === 'closed') {
        return 'Найдено'
    }

    return statusLabels[slot.status]
}

export const ProjectNeedSlotsManager = ({ projectId, kind, title, legacyItems = [], legacyTitle = 'Из текстового списка' }: ProjectNeedSlotsManagerProps) => {
    const [draft, setDraft] = useState<SlotDraft>(emptyDraft)
    const validProjectId = !Number.isNaN(projectId)

    const { data: allSlots = [], isLoading, isError } = useGetProjectNeedSlotsQuery(
        { projectId },
        { skip: !validProjectId }
    )
    const { data: competencies = [] } = useGetCompetenciesDictionaryQuery()
    const { data: resources = [] } = useGetResourcesDictionaryQuery()
    const { data: participation = [] } = useGetParticipationsDictionaryQuery()
    const [createSlot, { isLoading: isCreating }] = useCreateProjectNeedSlotMutation()
    const [updateSlot, { isLoading: isUpdating }] = useUpdateProjectNeedSlotMutation()
    const [deleteSlot, { isLoading: isDeleting }] = useDeleteProjectNeedSlotMutation()

    const slots = useMemo(() => allSlots.filter((slot) => slot.kind === kind), [allSlots, kind])
    const legacySuggestions = useMemo(() => {
        const usedTitles = new Set(slots.map((slot) => normalizedKey(slot.title)))
        const seenTitles = new Set<string>()

        return legacyItems
            .map((item, index) => ({
                id: `${index}:${normalizeText(item.title)}`,
                title: normalizeText(item.title),
                description: normalizeText(item.description),
            }))
            .filter((item) => item.title.length > 0)
            .filter((item) => {
                const key = normalizedKey(item.title)
                if (usedTitles.has(key) || seenTitles.has(key)) {
                    return false
                }
                seenTitles.add(key)
                return true
            })
    }, [legacyItems, slots])
    const isSubmitting = isCreating || isUpdating || isDeleting
    const closeLabel = kind === 'resource' ? 'Найдено' : 'Закрыть'

    const patchDraft = (patch: Partial<SlotDraft>) => setDraft((current) => ({ ...current, ...patch }))

    const fillFromLegacy = (item: { title: string; description: string }) => {
        patchDraft({
            title: item.title,
            description: item.description,
        })
    }

    const handleCreate = async () => {
        if (!validProjectId || draft.title.trim().length < 2) {
            toast.info('Добавьте название потребности')
            return
        }
        if (kind === 'role' && draft.competency_ids.length === 0) {
            toast.info('Выберите хотя бы одну компетенцию')
            return
        }
        if (kind === 'resource' && draft.resource_ids.length === 0) {
            toast.info('Выберите хотя бы один ресурс')
            return
        }

        try {
            await createSlot({
                projectId,
                body: {
                    kind,
                    status: 'open',
                    title: draft.title.trim(),
                    description: draft.description.trim() || null,
                    city: draft.city.trim() || null,
                    region: draft.region.trim() || null,
                    remote_allowed: kind === 'resource' ? draft.delivery_type === 'remote' : draft.remote_allowed,
                    meta: kind === 'resource' ? compactMeta(draft) : null,
                    competency_ids: draft.competency_ids,
                    resource_ids: draft.resource_ids,
                    participation_ids: kind === 'role' ? draft.participation_ids : [],
                },
            }).unwrap()
            setDraft(emptyDraft)
            toast.success('Потребность добавлена')
        } catch {
            toast.error('Не удалось добавить потребность')
        }
    }

    const handleStatus = async (slot: IProjectNeedSlotResource, status: IProjectNeedSlotStatus) => {
        try {
            await updateSlot({ projectId, slotId: slot.id, body: { status } }).unwrap()
            toast.success('Потребность обновлена')
        } catch {
            toast.error('Не удалось обновить потребность')
        }
    }

    const handleDelete = async (slot: IProjectNeedSlotResource) => {
        try {
            await deleteSlot({ projectId, slotId: slot.id }).unwrap()
            toast.success('Потребность удалена')
        } catch {
            toast.error('Не удалось удалить потребность')
        }
    }

    return (
        <Card variant="primaryCard" sx={CardStyle}>
            <Stack gap={2}>
                <Typography variant="black-16-semiBold">{title}</Typography>

                {isLoading ? (
                    <Stack alignItems="center" py={2}>
                        <CircularProgress size={24} />
                    </Stack>
                ) : isError ? (
                    <Typography variant="body2" color="error">
                        Не удалось загрузить структурированные потребности
                    </Typography>
                ) : slots.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        Структурированных потребностей пока нет
                    </Typography>
                ) : (
                    <Stack gap={1}>
                        {slots.map((slot) => {
                            const details = resourceDetails(slot)

                            return (
                            <Box
                                key={slot.id}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 1.5,
                                    minWidth: 0,
                                    maxWidth: '100%',
                                    overflowWrap: 'anywhere',
                                    wordBreak: 'break-word',
                                }}
                            >
                                <Stack gap={1} sx={{ minWidth: 0 }}>
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        justifyContent="space-between"
                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                        gap={1}
                                        sx={{ minWidth: 0 }}
                                    >
                                        <Stack gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography variant="black-16-semiBold" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                                                {slot.title}
                                            </Typography>
                                            {slot.description && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                                                >
                                                    {slot.description}
                                                </Typography>
                                            )}
                                        </Stack>
                                        <Chip
                                            label={statusLabel(slot)}
                                            color={statusColors[slot.status]}
                                            size="small"
                                            sx={{ alignSelf: 'flex-start', width: 'fit-content', maxWidth: '100%', flexShrink: 0 }}
                                        />
                                    </Stack>
                                    <SlotDictionarySummary slot={slot} />
                                    {slot.kind === 'resource' && slot.meta?.requirements && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                                        >
                                            {slot.meta.requirements}
                                        </Typography>
                                    )}
                                    {slot.kind === 'resource' && details.length > 0 && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                                        >
                                            {details.join(' · ')}
                                        </Typography>
                                    )}
                                    {slot.kind === 'role' && (slot.city || slot.region || slot.remote_allowed) && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                                        >
                                            {[slot.city, slot.region, slot.remote_allowed ? 'Можно удалённо' : null].filter(Boolean).join(', ')}
                                        </Typography>
                                    )}
                                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ pt: 0.5 }}>
                                        {slot.status !== 'open' && (
                                            <Button
                                                type="button"
                                                variant="action"
                                                size="small"
                                                disabled={isSubmitting}
                                                onClick={() => handleStatus(slot, 'open')}
                                                sx={actionButtonSx}
                                            >
                                                Открыть
                                            </Button>
                                        )}
                                        {slot.status === 'open' && (
                                            <Button
                                                type="button"
                                                variant="follow"
                                                size="small"
                                                disabled={isSubmitting}
                                                onClick={() => handleStatus(slot, 'paused')}
                                                sx={actionButtonSx}
                                            >
                                                На паузу
                                            </Button>
                                        )}
                                        {slot.status !== 'closed' && (
                                            <Button
                                                type="button"
                                                variant="action"
                                                size="small"
                                                disabled={isSubmitting}
                                                onClick={() => handleStatus(slot, 'closed')}
                                                sx={actionButtonSx}
                                            >
                                                {closeLabel}
                                            </Button>
                                        )}
                                        <Button
                                            type="button"
                                            variant="danger"
                                            size="small"
                                            disabled={isSubmitting}
                                            onClick={() => handleDelete(slot)}
                                            sx={actionButtonSx}
                                        >
                                            Удалить
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Box>
                        )})}
                    </Stack>
                )}

                <Stack gap={1.5}>
                    <Typography variant="black-16-semiBold">Новая потребность</Typography>
                    {legacySuggestions.length > 0 && (
                        <Stack gap={1}>
                            <Typography variant="body2" color="text.secondary">
                                {legacyTitle}
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                {legacySuggestions.map((item) => (
                                    <Button
                                        key={item.id}
                                        type="button"
                                        variant="outlined"
                                        size="small"
                                        disabled={isSubmitting}
                                        onClick={() => fillFromLegacy(item)}
                                    >
                                        {item.title}
                                    </Button>
                                ))}
                            </Stack>
                        </Stack>
                    )}
                    <FieldBlock label="Название">
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Название..."
                            value={draft.title}
                            onChange={(event) => patchDraft({ title: event.target.value })}
                            inputProps={{ maxLength: 60 }}
                        />
                    </FieldBlock>
                    <FieldBlock label="Описание">
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Описание..."
                            value={draft.description}
                            onChange={(event) => patchDraft({ description: event.target.value })}
                            multiline
                            minRows={2}
                        />
                    </FieldBlock>
                    {kind === 'role' ? (
                        <>
                            <FieldBlock label="Компетенции">
                                <DictionaryMultiSelectControl
                                    dictionary={competencies}
                                    placeholder="Выберите из списка"
                                    value={draft.competency_ids}
                                    onChange={(ids) => patchDraft({ competency_ids: ids })}
                                />
                            </FieldBlock>
                            <FieldBlock label="Условия участия">
                                <DictionaryMultiSelectControl
                                    dictionary={participation}
                                    placeholder="Выберите из списка"
                                    value={draft.participation_ids}
                                    onChange={(ids) => patchDraft({ participation_ids: ids })}
                                />
                            </FieldBlock>
                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                                <Box sx={fieldRowItemSx}>
                                    <FieldBlock label="Город">
                                        <TextField
                                            size="small"
                                            placeholder="Город"
                                            value={draft.city}
                                            onChange={(event) => patchDraft({ city: event.target.value })}
                                            fullWidth
                                            sx={singleLineFieldSx}
                                        />
                                    </FieldBlock>
                                </Box>
                                <Box sx={fieldRowItemSx}>
                                    <AutocompleteSelectField
                                        label="Регион"
                                        value={draft.region}
                                        options={russiaRegionsOptions}
                                        onChange={(value) => patchDraft({ region: value })}
                                        placeholder="Выберите регион"
                                    />
                                </Box>
                            </Stack>
                            <FormControlLabel
                                control={<Switch checked={draft.remote_allowed} onChange={(event) => patchDraft({ remote_allowed: event.target.checked })} />}
                                label="Можно удалённо"
                            />
                        </>
                    ) : (
                        <>
                            <FieldBlock label="Категория ресурса">
                                <DictionaryMultiSelectControl
                                    dictionary={resources}
                                    placeholder="Выберите из списка"
                                    value={draft.resource_ids}
                                    onChange={(ids) => patchDraft({ resource_ids: ids })}
                                />
                            </FieldBlock>
                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                                <Box sx={fieldRowItemSx}>
                                    <FieldBlock label="Количество">
                                        <TextField
                                            size="small"
                                            placeholder="Количество"
                                            value={draft.quantity}
                                            onChange={(event) => patchDraft({ quantity: event.target.value })}
                                            fullWidth
                                            sx={singleLineFieldSx}
                                        />
                                    </FieldBlock>
                                </Box>
                                <Box sx={fieldRowItemSx}>
                                    <AutocompleteSelectField
                                        label="Формат получения"
                                        value={draft.acquisition_type}
                                        options={resourceAcquisitionOptions}
                                        onChange={(value) => patchDraft({ acquisition_type: value })}
                                        placeholder="Выберите формат получения"
                                    />
                                </Box>
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                                <Box sx={fieldRowItemSx}>
                                    <AutocompleteSelectField
                                        label="Срок"
                                        value={draft.duration_type}
                                        options={resourceDurationOptions}
                                        onChange={(value) => patchDraft({ duration_type: value })}
                                        placeholder="Выберите срок"
                                    />
                                </Box>
                                <Box sx={fieldRowItemSx}>
                                    <AutocompleteSelectField
                                        label="Передача/использование"
                                        value={draft.delivery_type}
                                        options={resourceDeliveryOptions}
                                        onChange={(value) => patchDraft({ delivery_type: value })}
                                        placeholder="Выберите способ передачи"
                                    />
                                </Box>
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                                <Box sx={fieldRowItemSx}>
                                    <FieldBlock label="Город">
                                        <TextField
                                            size="small"
                                            placeholder="Город"
                                            value={draft.city}
                                            onChange={(event) => patchDraft({ city: event.target.value })}
                                            fullWidth
                                            sx={singleLineFieldSx}
                                        />
                                    </FieldBlock>
                                </Box>
                                <Box sx={fieldRowItemSx}>
                                    <AutocompleteSelectField
                                        label="Регион"
                                        value={draft.region}
                                        options={russiaRegionsOptions}
                                        onChange={(value) => patchDraft({ region: value })}
                                        placeholder="Выберите регион"
                                    />
                                </Box>
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                                <Box sx={fieldRowItemSx}>
                                    <AutocompleteSelectField
                                        label="Бюджет"
                                        value={draft.budget_type}
                                        options={resourceBudgetOptions}
                                        onChange={(value) => patchDraft({ budget_type: value })}
                                        placeholder="Выберите бюджет"
                                    />
                                </Box>
                                <Box sx={fieldRowItemSx}>
                                    <FieldBlock label="Комментарий к бюджету">
                                        <TextField
                                            size="small"
                                            placeholder="Комментарий к бюджету"
                                            value={draft.budget_comment}
                                            onChange={(event) => patchDraft({ budget_comment: event.target.value })}
                                            fullWidth
                                            sx={singleLineFieldSx}
                                        />
                                    </FieldBlock>
                                </Box>
                            </Stack>
                            <FieldBlock label="Требования и условия">
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="Требования и условия"
                                    value={draft.requirements}
                                    onChange={(event) => patchDraft({ requirements: event.target.value })}
                                    multiline
                                    minRows={2}
                                />
                            </FieldBlock>
                        </>
                    )}
                    <Button type="button" variant="follow" disabled={isSubmitting} onClick={handleCreate}>
                        {isCreating ? 'Добавление...' : 'Добавить потребность'}
                    </Button>
                </Stack>
            </Stack>
        </Card>
    )
}
